import { useState } from 'react'
import { SignIn } from './SignIn.js'
import { TwoFactorSetup } from './TwoFactorSetup.js'
import { SessionExpired } from './SessionExpired.js'
import { PasswordResetRequest, PasswordResetConfirm } from './PasswordReset.js'
import './authStyles.css'

/**
 * Auth flow container.
 *
 * Deliberately a local state machine rather than a router. FOUNDATION has no
 * routing library and the real navigation structure is §13's job (top nav plus
 * sub nav), so introducing one here would pre-empt that decision for the sake
 * of five screens that are a linear flow anyway.
 *
 * INBOX replaces this with real routes. The screens themselves take plain
 * callbacks, so they move across unchanged.
 */

export type AuthScreen =
  | 'signIn'
  | 'twoFactorSetup'
  | 'sessionExpired'
  | 'passwordResetRequest'
  | 'passwordResetConfirm'

type Props = {
  initial?: AuthScreen
  /** Captured before redirecting to sign in, restored afterwards. */
  returnTo?: string
  /**
   * Called once a session exists. **REQUIRED.**
   *
   * ┌─ ⛔ THIS WAS `onAuthenticated?:` AND IT MADE A SUCCESSFUL LOGIN A NO-OP ──┐
   * │ `App.tsx` is the only caller and never passed it, so                      │
   * │ `onAuthenticated?.(to)` resolved to `undefined?.()`. On 2026-07-30 the    │
   * │ first real account signed in FOUR TIMES in three minutes: the server      │
   * │ logged `login succeeded` each time and issued seven sessions, while the   │
   * │ form showed no error and did not move.                                   │
   * │                                                                          │
   * │ THE COMPILER HAD NOTHING TO SAY, because optional means omitting it is a  │
   * │ valid call. Required-and-nullable would have made it a compile error.     │
   * │                                                                          │
   * │ SECOND INSTANCE OF THE `media?:` SHAPE, three days after the first, which │
   * │ dropped the first photo ever sent to this system. The rule recorded then: │
   * │ OPTIONAL IS JUSTIFIED ONLY WHEN ABSENCE MEANS SOMETHING NULL CANNOT       │
   * │ EXPRESS. `onAuthenticated` has no such meaning - "nothing happens when a  │
   * │ user signs in" is not a state anyone wanted.                             │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  onAuthenticated: (returnTo?: string) => void
}

export function AuthFlow({
  initial = 'signIn',
  returnTo,
  onAuthenticated,
}: Props): React.JSX.Element {
  const [screen, setScreen] = useState<AuthScreen>(initial)

  switch (screen) {
    case 'sessionExpired':
      return (
        <SessionExpired
          {...(returnTo !== undefined ? { returnTo } : {})}
          onSignIn={() => setScreen('signIn')}
        />
      )

    case 'twoFactorSetup':
      return (
        <TwoFactorSetup
          // Was `onAuthenticated?.(returnTo)`. The `?.` was the SAME defect on a
          // second path: completing 2FA setup also went nowhere. Both are now
          // plain calls, because the prop is required.
          onComplete={() => onAuthenticated(returnTo)}
          onCancel={() => setScreen('signIn')}
        />
      )

    case 'passwordResetRequest':
      return <PasswordResetRequest onBack={() => setScreen('signIn')} />

    case 'passwordResetConfirm':
      return <PasswordResetConfirm onBack={() => setScreen('signIn')} />

    case 'signIn':
      return (
        <SignIn
          {...(returnTo !== undefined ? { returnTo } : {})}
          onSignedIn={(to) => onAuthenticated(to)}
          onForgotPassword={() => setScreen('passwordResetRequest')}
        />
      )
  }
}

/**
 * Capture the current location before sending someone to sign in.
 *
 * Exported so whatever detects a 401 can call it. Kept here rather than inside
 * SessionExpired because the capture has to happen at the moment of the 401,
 * which is before that screen renders.
 *
 * ┌─ ⛔ "WHATEVER" WAS INBOX, AND IT TOOK NINE DAYS. `returnto-had-no-consumer` ┐
 * │ in `ops/deferrals.txt`. This function was written in FOUNDATION, exported,  │
 * │ and documented with the exact caller it was waiting for - the sentence      │
 * │ above - and **nothing called it until 2026-08-08**. `App.tsx` is the        │
 * │ consumer; it detects the 401 through `data/unauthorized.ts`.                │
 * │                                                                            │
 * │ ⚠️ THE DESIGN WAS CORRECT AND UNREACHABLE, WHICH IS WORSE THAN WRONG. A     │
 * │ wrong design is found the first time it runs. This one had a comment naming │
 * │ its own missing consumer, and `SessionExpired.tsx` made a promise in prose  │
 * │ that nothing could falsify.                                                 │
 * │                                                                            │
 * │ ⛔ AND THE OBVIOUS TEST OF IT WOULD HAVE BEEN VACUOUS. The auth screen       │
 * │ renders IN PLACE, so the address bar still holds the conversation - the app │
 * │ lands back on it whether or not anything was captured. What makes this      │
 * │ function load-bearing is that **Back moves the URL underneath a screen that │
 * │ will not dismiss**. See `tests/web/SessionReturnTo.test.tsx`, which presses │
 * │ it twice for exactly that reason.                                          │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export function captureReturnTo(): string {
  return window.location.pathname + window.location.search
}
